<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import MultiselectDropdown from '../../../../../shared/components/ui/MultiselectDropdown.vue';

const props = defineProps({
  agents: {
    type: Array,
    default: () => [],
  },
  selectedAssigneeId: {
    type: [Number, String],
    default: null,
  },
  updateHistory: {
    type: Boolean,
    default: false,
  },
  ticketId: {
    type: [Number, String],
    default: null,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:selectedAssigneeId', 'change']);

const store = useStore();

const selectedAssignee = computed(() => {
  return (
    props.agents.find(agent => agent.id === props.selectedAssigneeId) || {}
  );
});

const handleAssigneeChange = async newAssignee => {
  if (newAssignee.id === props.selectedAssigneeId) {
    // If the same assignee is selected, remove it
    emit('update:selectedAssigneeId', null);
    emit('change', null);

    // If updateHistory is true, also call the remove function
    if (props.updateHistory && props.ticketId) {
      await handleRemoveAssignee();
    }
    return;
  }

  emit('update:selectedAssigneeId', newAssignee.id);
  emit('change', newAssignee);

  // If updateHistory is true, update the ticket history
  if (props.updateHistory && props.ticketId) {
    try {
      const response = await store.dispatch('tickets/changeAssignee', {
        id: props.ticketId,
        newAssignee: newAssignee.id,
      });

      if (response && response.assignee_name) {
        store.commit('tickets/UPDATE_TICKET_ASSIGNEE', {
          id: props.ticketId,
          assignee_name: response.assignee_name,
          assignee_id: response.assignee_id,
        });
      }

      // Reload ticket to ensure status label is updated after automatic status change
      await store.dispatch('tickets/getTicket', props.ticketId);
      // Refresh the ticket history immediately after the change
      await store.dispatch('tickets/getTicketHistory', props.ticketId);
    } catch (error) {
      console.error('Error updating assignee:', error);
      // Revert the change on error
      emit('update:selectedAssigneeId', props.selectedAssigneeId);
    }
  }
};

const handleRemoveAssignee = async () => {
  if (!props.updateHistory || !props.ticketId) return;

  try {
    const response = await store.dispatch('tickets/removeAssignee', {
      id: props.ticketId,
    });

    if (response) {
      emit('update:selectedAssigneeId', null);
      emit('change', null);
    }

    // Reload ticket to ensure status label is updated after automatic status change
    await store.dispatch('tickets/getTicket', props.ticketId);
    // Refresh the ticket history immediately after the change
    await store.dispatch('tickets/getTicketHistory', props.ticketId);
  } catch (error) {
    console.error('Error removing assignee:', error);
  }
};
</script>

<template>
  <div>
    <div @click.prevent>
      <MultiselectDropdown
        :options="agents"
        :selected-item="selectedAssignee"
        multiselector-title="Selecionar Responsável"
        multiselector-placeholder="Selecionar usuário"
        input-placeholder="Buscar usuário..."
        no-search-result="Nenhum usuário encontrado"
        :has-thumbnail="false"
        @select="handleAssigneeChange"
      />
    </div>
  </div>
</template>
